import React, { useContext, useEffect } from "react";
import {
  Control,
  FieldErrors,
  UseFormSetValue,
  useFieldArray,
} from "react-hook-form";
import { Typography, Switch, Grid, useTheme } from "@mui/material";

import TextInput from "../../../main/components/TextInput";
import { UsinasContext } from "../context/UsinasContextProvider";
import NumberInput from "../../../main/components/NumberInput";
import DangerBaseButton from "../../../main/components/DangerBaseButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import PhoneNumberInput from "../../../main/components/PhoneNumberInput";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

export function UGsFormContent(props: {
  index: number;
  setValue: UseFormSetValue<any>;
  control: Control<any, any>;
  errors: FieldErrors<any>;
  removeUG: (index: number) => void;
  openUGs: string[];
  setOpenUGs: (open: string[]) => void;
}): React.ReactNode {
  const { control, errors, index, removeUG, openUGs, setOpenUGs } = props;
  const [hasTrackers, setHasTrackers] = React.useState(true);
  const { usina, newUsina } = useContext(UsinasContext);
  const theme = useTheme();
  const {
    fields: invertersProducersFields,
    append: appendInvertersProducer,
    remove: removeInvertersProducer,
  } = useFieldArray({
    control,
    name: `ugs.${index}.invertersProducers`,
  });
  const {
    fields: trackersProducersFields,
    append: appendTrackersProducer,
    remove: removeTrackersProducer,
  } = useFieldArray({
    control,
    name: `ugs.${index}.trackersProducers`,
  });
  const {
    fields: modulesProducersFields,
    append: appendModulesProducer,
    remove: removeModulesProducer,
  } = useFieldArray({
    control,
    name: `ugs.${index}.modulesProducers`,
  });
  useEffect(() => {
    const path = window.location.pathname;
    const isRegister = path.includes("cadastrar");
    props.setValue(`ugs.${index}.number`, index + 1);
    if (isRegister) {
      if (!hasTrackers) {
        props.setValue(`ugs.${index}.trackersProducers`, undefined);
        props.setValue(`ugs.${index}.trackersAmount`, undefined);
      } else {
        props.setValue(`ugs.${index}.trackersProducers`, [{}]);
      }
    }
  }, [hasTrackers]);
  useEffect(() => {
    if (!usina?.uGs) return;
    if (usina?.uGs[index]?.components?.trackers) {
      setHasTrackers(true);
    } else {
      setHasTrackers(false);
    }
    if (usina?.uGs[index]?.hasTransformer) {
      setHasTransformer(true);
    } else {
      setHasTransformer(false);
    }
    if (usina?.uGs[index]?.hasSmartLogger) {
      setHasSmartLogger(true);
    } else {
      setHasSmartLogger(false);
    }
  }, []);

  const [hasTransformer, setHasTransformer] = React.useState(true);
  const [hasSmartLogger, setHasSmartLogger] = React.useState(true);

  useEffect(() => {
    props.setValue(`ugs.${index}.hasTransformer`, hasTransformer);
  }, [hasTransformer]);

  useEffect(() => {
    props.setValue(`ugs.${index}.hasSmartLogger`, hasSmartLogger);
  }, [hasSmartLogger]);

  useEffect(() => {
    setHasTransformer(newUsina?.uGs?.find((ug) => ug?.number === index + 1)?.hasTransformer ?? false);
    setHasSmartLogger(newUsina?.uGs?.find((ug) => ug?.number === index + 1)?.hasSmartLogger ?? false);
  }, [newUsina]);



  const open = openUGs.includes(`UG ${index + 1}`);

  function toggleOpen() {
    if (open) {
      setOpenUGs(openUGs.filter((ug) => ug !== `UG ${index + 1}`));
    } else {
      setOpenUGs([...openUGs, `UG ${index + 1}`]);
    }
  }

  return (
    <>
      <Grid onClick={() => toggleOpen()} item xs={12} textAlign={"start"} sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", backgroundColor: "rgba(217, 217, 217, 0.1)", marginLeft: "16px", cursor: "pointer", borderRadius: 2, paddingY: "16px", paddingRight: "16px" }}>
        <Typography
          variant="h6"
        >
          UG {index + 1}
        </Typography>
        <KeyboardArrowRightIcon
          sx={{
            height: "32px",
            width: "32px",
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 0.1s ease-in-out"
          }}
        />
      </Grid>
      {open ? <>
        <Grid
          display={"flex"}
          item
          xs={12}
          md={3}
          sx={{ justifyContent: "center" }}
        >
          <NumberInput
            sx={{ width: "100%", maxWidth: "280px" }}
            control={control}
            fieldName={`ugs.${index}.number`}
            label="Número da UG"
            errors={errors}
            inputProps={{
              min: 0,
            }}
          // width={200}
          />
        </Grid>

        <Grid display={"flex"} item xs={12} md={3}>
          <TextInput
            control={control}
            fieldName={`ugs.${index}.disjuntorTag`}
            label="Tag do disjuntor"
            errors={errors}
            sx={{ width: "100%", maxWidth: "280px" }}
          />
        </Grid>

        <Grid item xs={12} md={3} display={"flex"}>
          <Grid>
            <Typography variant="subtitle1" sx={{ marginTop: "15px" }}>
              A UG possui transformador?
            </Typography>
            Não
            <Switch
              checked={hasTransformer}
              onChange={(_, checked) => {
                setHasTransformer(checked);
              }}
            />
            Sim
          </Grid>
        </Grid>
        <Grid item xs={12} md={3} display={"flex"}>
          <Grid>
            <Typography variant="subtitle1" sx={{ marginTop: "15px" }}>
              A UG possui smart logger?
            </Typography>
            Não
            <Switch
              checked={hasSmartLogger}
              onChange={(_, checked) => {
                setHasSmartLogger(checked);
              }}
            />
            Sim
          </Grid>
        </Grid>
        <Grid item xs={12} textAlign={"start"}>
          <Typography
            variant="h6"
            sx={{
              borderBottom: "1px solid #ccc",
              paddingBottom: 1,
              marginBottom: "15px",
            }}
          >
            Inversores
          </Typography>
        </Grid>
        <Grid
          display={"flex"}
          item
          xs={12}
          md={3}
          sx={{ justifyContent: "center" }}
        >
          <NumberInput
            sx={{
              width: "100%",
              maxWidth: "280px",
            }}
            control={control}
            fieldName={`ugs.${index}.invertersAmount`}
            label="N° de inversores"
            errors={errors}
            width={150}
          />
        </Grid>
        <Grid
          display={"flex"}
          item
          xs={12}
          md={3}
          sx={{ justifyContent: "center" }}
        >
          <NumberInput
            sx={{
              width: "100%",
              maxWidth: "280px",
            }}
            control={control}
            fieldName={`ugs.${index}.inverterPower`}
            label="Potência Inversor"
            errors={errors}
          // width={150}
          />
        </Grid>
        <>
          {invertersProducersFields.map((field, producerIndex) => (
            <React.Fragment key={field.id}>
              <Grid item display={"flex"} xs={12}>
                <Typography variant="subtitle1" sx={{ marginTop: "15px" }}>
                  Fabricante {producerIndex + 1}:
                </Typography>
              </Grid>
              <Grid
                display={"flex"}
                item
                xs={12}
                md={3}
                sx={{ justifyContent: "center" }}
              >
                <PhoneNumberInput
                  sx={{ width: "100%", maxWidth: "280px" }}
                  control={control}
                  fieldName={`ugs.${index}.invertersProducers[${producerIndex}].producerContactPhone`}
                  label="Telefone de Contato"
                  errors={errors}
                />
              </Grid>
              <Grid
                display={"flex"}
                item
                xs={12}
                md={3}
                sx={{ justifyContent: "center" }}
              >
                <TextInput
                  sx={{ width: "100%", maxWidth: "280px" }}
                  control={control}
                  fieldName={`ugs.${index}.invertersProducers[${producerIndex}].producerContactEmail`}
                  label="Email de Contato"
                  errors={errors}
                />
              </Grid>
              <Grid
                display={"flex"}
                item
                xs={12}
                md={3}
                sx={{ justifyContent: "center" }}
              >
                <TextInput
                  sx={{ width: "100%", maxWidth: "280px" }}
                  control={control}
                  fieldName={`ugs.${index}.invertersProducers[${producerIndex}].producerPortalAccessLink`}
                  label="Link de Acesso ao Portal"
                  errors={errors}
                />
              </Grid>
              <Grid
                display={"flex"}
                item
                xs={12}
                md={3}
                sx={{ justifyContent: "center" }}
              >
                <TextInput
                  sx={{ width: "100%", maxWidth: "280px" }}
                  control={control}
                  fieldName={`ugs.${index}.invertersProducers[${producerIndex}].producerPortalAccessUsername`}
                  label="Usuário de Acesso ao Portal"
                  errors={errors}
                />
              </Grid>
              <Grid
                display={"flex"}
                item
                xs={12}
                md={3}
                sx={{ justifyContent: "center" }}
              >
                <TextInput
                  sx={{ width: "100%", maxWidth: "280px" }}
                  control={control}
                  fieldName={`ugs.${index}.invertersProducers[${producerIndex}].producerPortalAccessPassword`}
                  label="Senha de Acesso ao Portal"
                  errors={errors}
                />
              </Grid>
              <Grid item display={"flex"} xs={12}>
                {producerIndex > 0 && (
                  <DangerBaseButton
                    onClick={() => {
                      removeInvertersProducer(producerIndex);
                    }}
                    title="Remover"
                    // title="Remover Fabricante"
                    startIcon={<DeleteIcon />}
                  />
                )}
              </Grid>
            </React.Fragment>
          ))}
          <Grid
            item
            display={"flex"}
            xs={12}
            style={{
              justifyContent: "center",
            }}
          // style={{
          //   justifyContent: "center",
          //   [theme.breakpoints.up("md")]: { justifyContent: "flex-end" },
          // }}
          >
            <PrimaryBaseButton
              onClick={() => {
                appendInvertersProducer({});
              }}
              title="Adicionar Fabricante"
              startIcon={<AddIcon />}
              // sx={{ height: "38px" }}
              sx={{
                height: "38px",
                [theme.breakpoints.up("md")]: { marginLeft: "auto" },
              }}
            />
          </Grid>
        </>
        <Grid item xs={12} textAlign={"start"}>
          <Typography
            variant="h6"
            sx={{
              borderBottom: "1px solid #ccc",
              paddingBottom: 1,
              marginBottom: "15px",
              marginTop: "30px",
            }}
          >
            Strings
          </Typography>
        </Grid>
        <Grid
          display={"flex"}
          item
          xs={12}
          md={3}
          sx={{ justifyContent: "center" }}
        >
          <NumberInput
            sx={{
              width: "100%",
              maxWidth: "280px",
            }}
            control={control}
            fieldName={`ugs.${index}.stringAmount`}
            label="Quantidade"
            errors={errors}
          // width={150}
          />
        </Grid>
        <Grid
          display={"flex"}
          item
          xs={12}
          md={3}
          sx={{ justifyContent: "center" }}
        >
          <NumberInput
            sx={{
              width: "100%",
              maxWidth: "280px",
            }}
            control={control}
            fieldName={`ugs.${index}.stringPower`}
            label="Potência"
            errors={errors}
          // width={150}
          />
        </Grid>

        <Grid item xs={12} display={"flex"}>
          <Grid>
            <Typography variant="subtitle1" sx={{ marginTop: "15px" }}>
              A UG possui Trackers?
            </Typography>
            Não
            <Switch
              checked={hasTrackers}
              onChange={(_, checked) => {
                setHasTrackers(checked);
              }}
            />
            Sim
          </Grid>
        </Grid>

        {hasTrackers && (
          <>
            <Grid item xs={12} textAlign={"start"}>
              <Typography
                variant="h6"
                sx={{
                  borderBottom: "1px solid #ccc",
                  paddingBottom: 1,
                  marginBottom: "15px",
                  marginTop: "30px",
                }}
              >
                Trackers
              </Typography>
            </Grid>
            <Grid
              display={"flex"}
              item
              xs={12}
              md={3}
              sx={{ justifyContent: "center" }}
            >
              <NumberInput
                sx={{
                  width: "100%",
                  maxWidth: "280px",
                }}
                control={control}
                fieldName={`ugs.${index}.trackersAmount`}
                label="N° de Trackers"
                errors={errors}
              // width={150}
              />
            </Grid>
            {trackersProducersFields.map((field, producerIndex) => (
              <React.Fragment key={field.id}>
                <Grid item display={"flex"} xs={12}>
                  <Typography variant="subtitle1" sx={{ marginTop: "15px" }}>
                    Fabricante {producerIndex + 1}:
                  </Typography>
                </Grid>
                <Grid
                  display={"flex"}
                  item
                  xs={12}
                  md={3}
                  sx={{ justifyContent: "center" }}
                >
                  <PhoneNumberInput
                    sx={{ width: "100%", maxWidth: "280px" }}
                    control={control}
                    fieldName={`ugs.${index}.trackersProducers[${producerIndex}].producerContactPhone`}
                    label="Telefone de Contato"
                    errors={errors}
                  />
                </Grid>
                <Grid
                  display={"flex"}
                  item
                  xs={12}
                  md={3}
                  sx={{ justifyContent: "center" }}
                >
                  <TextInput
                    sx={{ width: "100%", maxWidth: "280px" }}
                    control={control}
                    fieldName={`ugs.${index}.trackersProducers[${producerIndex}].producerContactEmail`}
                    label="Email de Contato"
                    errors={errors}
                  />
                </Grid>
                <Grid
                  display={"flex"}
                  item
                  xs={12}
                  md={3}
                  sx={{ justifyContent: "center" }}
                >
                  <TextInput
                    sx={{ width: "100%", maxWidth: "280px" }}
                    control={control}
                    fieldName={`ugs.${index}.trackersProducers[${producerIndex}].producerPortalAccessLink`}
                    label="Link de Acesso ao Portal"
                    errors={errors}
                  />
                </Grid>
                <Grid
                  display={"flex"}
                  item
                  xs={12}
                  md={3}
                  sx={{ justifyContent: "center" }}
                >
                  <TextInput
                    sx={{ width: "100%", maxWidth: "280px" }}
                    control={control}
                    fieldName={`ugs.${index}.trackersProducers[${producerIndex}].producerPortalAccessUsername`}
                    label="Usuário de Acesso ao Portal"
                    errors={errors}
                  />
                </Grid>
                <Grid
                  display={"flex"}
                  item
                  xs={12}
                  md={3}
                  sx={{ justifyContent: "center" }}
                >
                  <TextInput
                    sx={{
                      width: "100%",
                      maxWidth: "280px",
                      marginBottom: "20px",
                    }}
                    control={control}
                    fieldName={`ugs.${index}.trackersProducers[${producerIndex}].producerPortalAccessPassword`}
                    label="Senha de Acesso ao Portal"
                    errors={errors}
                  />
                </Grid>
                {producerIndex > 0 && (
                  <Grid item display={"flex"} xs={12}>
                    <DangerBaseButton
                      onClick={() => {
                        removeTrackersProducer(producerIndex);
                      }}
                      title="Remover"
                      // title="Remover Fabricante"
                      startIcon={<DeleteIcon />}
                    />
                  </Grid>
                )}
              </React.Fragment>
            ))}
            <Grid
              item
              display={"flex"}
              xs={12}
              style={{
                justifyContent: "center",
              }}
            // style={{
            //   justifyContent: "center",
            //   [theme.breakpoints.up("md")]: { justifyContent: "flex-end" },
            // }}
            >
              <PrimaryBaseButton
                onClick={() => {
                  appendTrackersProducer({});
                }}
                title="Adicionar Fabricante"
                startIcon={<AddIcon />}
                // sx={{ height: "38px" }}
                sx={{
                  height: "38px",
                  [theme.breakpoints.up("md")]: { marginLeft: "auto" },
                }}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} textAlign={"start"}>
          <Typography
            variant="h6"
            sx={{
              borderBottom: "1px solid #ccc",
              paddingBottom: 1,
              marginBottom: "15px",
              marginTop: "30px",
            }}
          >
            Módulos
          </Typography>
        </Grid>
        <Grid
          display={"flex"}
          item
          xs={12}
          md={3}
          sx={{ justifyContent: "center" }}
        >
          <NumberInput
            sx={{
              width: "100%",
              maxWidth: "280px",
            }}
            control={control}
            fieldName={`ugs.${index}.modulesAmount`}
            label="N° de Módulos"
            errors={errors}
          // width={150}
          />
        </Grid>
        <Grid
          display={"flex"}
          item
          xs={12}
          md={3}
          sx={{ justifyContent: "center" }}
        >
          <NumberInput
            sx={{
              width: "100%",
              maxWidth: "280px",
            }}
            control={control}
            fieldName={`ugs.${index}.modulesPower`}
            label="Potência Instalada"
            errors={errors}
          // width={150}
          />
        </Grid>
        <>
          {modulesProducersFields.map((field, producerIndex) => (
            <React.Fragment key={field.id}>
              <Grid item display={"flex"} xs={12}>
                <Typography variant="subtitle1" sx={{ marginTop: "15px" }}>
                  Fabricante {producerIndex + 1}:
                </Typography>
              </Grid>
              <Grid
                display={"flex"}
                item
                xs={12}
                md={3}
                sx={{ justifyContent: "center" }}
              >
                <PhoneNumberInput
                  sx={{ width: "100%", maxWidth: "280px" }}
                  control={control}
                  fieldName={`ugs.${index}.modulesProducers[${producerIndex}].producerContactPhone`}
                  label="Telefone de Contato"
                  errors={errors}
                />
              </Grid>
              <Grid
                display={"flex"}
                item
                xs={12}
                md={3}
                sx={{ justifyContent: "center" }}
              >
                <TextInput
                  sx={{
                    width: "100%",
                    maxWidth: "280px",
                    marginBottom: "10px",
                    marginTop: "10px",
                  }}
                  control={control}
                  fieldName={`ugs.${index}.modulesProducers[${producerIndex}].producerContactEmail`}
                  label="Email de Contato"
                  errors={errors}
                />
              </Grid>
              {producerIndex > 0 && (
                <Grid item display={"flex"} xs={12}>
                  <DangerBaseButton
                    onClick={() => {
                      removeModulesProducer(producerIndex);
                    }}
                    title="Remover "
                    // title="Remover Fabricante"
                    startIcon={<DeleteIcon />}
                  />
                </Grid>
              )}
            </React.Fragment>
          ))}
          <Grid
            item
            display={"flex"}
            xs={12}
            style={{
              justifyContent: "center",
            }}
          // style={{
          //   justifyContent: "center",
          //   [theme.breakpoints.up("md")]: { justifyContent: "flex-end" },
          // }}
          >
            <PrimaryBaseButton
              onClick={() => {
                appendModulesProducer({});
              }}
              title="Adicionar Fabricante"
              startIcon={<AddIcon />}
              sx={{
                height: "38px",
                [theme.breakpoints.up("md")]: { marginLeft: "auto" },
              }}
            // sx={{ height: "38px" }}
            />
          </Grid>
        </>
      </> : <></>}
      <Grid item display={"flex"} xs={12}>
        {index > 0 && (
          <DangerBaseButton
            onClick={() => {
              removeUG(index);
            }}
            title="Remover UG"
            startIcon={<DeleteIcon />}
          />
        )}
      </Grid>
    </>
  );
}
